import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ShieldCheck, UserCircle, MapPin, Tag, Users, ScanLine,
    CheckCircle2, Circle, Clock, AlertTriangle, ChevronRight,
    Building2, Loader2, ArrowRight,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { providerKycApi } from "@/lib/api/kyc";
import { providerApi } from "@/lib/api/provider";
import { providerOffersApi } from "@/lib/api/offers";

// ── helpers ──────────────────────────────────────────────────────────────────

function KycStatusBanner({ status, navigate, t }) {
    if (status === "APPROVED") return (
        <div className="rounded-2xl border border-green-200 bg-green-50 dark:border-green-900/40 dark:bg-green-950/20 p-5 flex items-start gap-4">
            <ShieldCheck className="h-6 w-6 text-green-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground">{t("providerDash.kycApprovedTitle")}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{t("providerDash.kycApprovedBody")}</p>
            </div>
        </div>
    );

    if (status === "PENDING") return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20 p-5 flex items-start gap-4">
            <Clock className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground">{t("providerDash.kycPendingTitle")}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{t("providerDash.kycPendingBody")}</p>
            </div>
        </div>
    );

    if (status === "REJECTED") return (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5 flex items-start gap-4">
            <AlertTriangle className="h-6 w-6 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground">{t("providerDash.kycRejectedTitle")}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{t("providerDash.kycRejectedBody")}</p>
            </div>
            <button
                onClick={() => navigate("/provider/kyc")}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-destructive px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
            >
                {t("providerDash.fixDocuments")}
                <ChevronRight className="h-3.5 w-3.5" />
            </button>
        </div>
    );

    // DRAFT
    return (
        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 flex items-start gap-4">
            <ShieldCheck className="h-6 w-6 text-primary shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground">{t("providerDash.kycDraftTitle")}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{t("providerDash.kycDraftBody")}</p>
            </div>
            <button
                onClick={() => navigate("/provider/kyc")}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
            >
                {t("providerDash.startKyc")}
                <ChevronRight className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}

function SetupStep({ done, icon: Icon, title, body, action, onClick, disabled }) {
    return (
        <div className={`surface-card p-4 flex items-start gap-4 transition-opacity ${disabled ? "opacity-40" : ""}`}>
            <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-green-100 dark:bg-green-950" : "bg-muted"}`}>
                {done
                    ? <CheckCircle2 className="h-4.5 w-4.5 text-green-600" />
                    : <Icon className="h-4 w-4 text-muted-foreground" />
                }
            </div>
            <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${done ? "line-through text-muted-foreground" : "text-foreground"}`}>{title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{body}</p>
            </div>
            {!done && !disabled && (
                <button
                    onClick={onClick}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline underline-offset-2"
                >
                    {action}
                    <ArrowRight className="h-3 w-3" />
                </button>
            )}
        </div>
    );
}

function QuickLink({ icon: Icon, label, to, navigate }) {
    return (
        <button
            onClick={() => navigate(to)}
            className="surface-card p-4 flex items-center gap-3 hover:bg-accent transition-colors text-start w-full group"
        >
            <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4 text-primary" />
            </div>
            <span className="text-sm font-medium flex-1">{label}</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>
    );
}

// ── main ─────────────────────────────────────────────────────────────────────

export default function ProviderDashboardPage() {
    const { t } = useI18n();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [kyc, setKyc] = useState(null);
    const [provider, setProvider] = useState(null);
    const [offerCount, setOfferCount] = useState(null);
    const [locationCount, setLocationCount] = useState(null);

    useEffect(() => {
        Promise.all([
            providerKycApi.summary(),
            providerApi.me(),
            providerOffersApi.list().catch(() => []),
            providerApi.listLocations().catch(() => []),
        ]).then(([kycData, provData, offers, locations]) => {
            setKyc(kycData);
            setProvider(provData);
            setOfferCount(offers.length);
            setLocationCount(locations.length);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
    );

    const kycStatus = kyc?.provider?.kyc_status || "DRAFT";
    const isApproved = kycStatus === "APPROVED";
    const isPending  = kycStatus === "PENDING";

    const firstName = user?.full_name?.split(" ")[0] || t("providerDash.there");
    const orgName   = provider?.corporate_name || kyc?.provider?.corporate_name || "";

    // Setup checklist — steps locked until KYC submitted
    const kycDone      = isApproved || isPending;
    const profileDone  = !!(provider?.description_en || provider?.description_ar || provider?.logo_url);
    const locationDone = locationCount > 0;
    const offerDone    = offerCount > 0;
    const setupLocked  = kycStatus === "DRAFT"; // encourage KYC first

    const setupSteps = [
        {
            done: kycDone,
            icon: ShieldCheck,
            title: t("providerDash.stepKycTitle"),
            body:  t("providerDash.stepKycBody"),
            action: t("providerDash.stepKycAction"),
            onClick: () => navigate("/provider/kyc"),
            disabled: false,
        },
        {
            done: profileDone,
            icon: UserCircle,
            title: t("providerDash.stepProfileTitle"),
            body:  t("providerDash.stepProfileBody"),
            action: t("providerDash.stepProfileAction"),
            onClick: () => navigate("/provider/profile"),
            disabled: setupLocked && !kycDone,
        },
        {
            done: locationDone,
            icon: MapPin,
            title: t("providerDash.stepLocationTitle"),
            body:  t("providerDash.stepLocationBody"),
            action: t("providerDash.stepLocationAction"),
            onClick: () => navigate("/provider/locations"),
            disabled: setupLocked && !kycDone,
        },
        {
            done: offerDone,
            icon: Tag,
            title: t("providerDash.stepOfferTitle"),
            body:  t("providerDash.stepOfferBody"),
            action: t("providerDash.stepOfferAction"),
            onClick: () => navigate("/provider/offers"),
            disabled: setupLocked && !kycDone,
        },
    ];

    const completedSteps = setupSteps.filter(s => s.done).length;
    const pct = Math.round((completedSteps / setupSteps.length) * 100);

    return (
        <div className="max-w-3xl space-y-6 fade-up" data-testid="page-provider-dashboard">

            {/* Greeting */}
            <header>
                <h1 className="text-3xl font-semibold tracking-tight">
                    {t("providerDash.greeting", { name: firstName })}
                </h1>
                {orgName && (
                    <p className="mt-1 text-muted-foreground flex items-center gap-1.5">
                        <Building2 className="h-4 w-4 shrink-0" />
                        {orgName}
                    </p>
                )}
            </header>

            {/* KYC status banner */}
            <KycStatusBanner status={kycStatus} navigate={navigate} t={t} />

            {/* Setup checklist */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base font-semibold">{t("providerDash.setupTitle")}</h2>
                    <span className="text-sm text-muted-foreground">{completedSteps}/{setupSteps.length} {t("providerDash.setupDone")}</span>
                </div>

                {/* Progress bar */}
                <div className="h-1.5 w-full rounded-full bg-muted mb-4 overflow-hidden">
                    <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${pct}%` }}
                    />
                </div>

                <div className="space-y-2">
                    {setupSteps.map((step, i) => (
                        <SetupStep key={i} {...step} />
                    ))}
                </div>

                {pct === 100 && (
                    <div className="mt-3 rounded-2xl border border-green-200 bg-green-50 dark:border-green-900/40 dark:bg-green-950/20 px-4 py-3 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-400">{t("providerDash.allDone")}</span>
                    </div>
                )}
            </section>

            {/* Quick links — only shown once approved */}
            {isApproved && (
                <section>
                    <h2 className="text-base font-semibold mb-3">{t("providerDash.quickLinks")}</h2>
                    <div className="grid sm:grid-cols-2 gap-2">
                        <QuickLink icon={ScanLine}  label={t("nav.verify")} to="/provider/verify"    navigate={navigate} />
                        <QuickLink icon={Tag}        label={t("nav.offers")} to="/provider/offers"    navigate={navigate} />
                        <QuickLink icon={Users}      label={t("nav.staff")}  to="/provider/staff"     navigate={navigate} />
                        <QuickLink icon={MapPin}     label={t("nav.locations")} to="/provider/locations" navigate={navigate} />
                    </div>
                </section>
            )}

        </div>
    );
}
